import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import Link from 'next/link';
import cn from 'classnames';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
// Api
import { GET_MERCH_CART_ITEM_LIST } from 'api/merch/queries';
// Types
import { GetMerchCartItemsList } from 'api/merch/types/GetMerchCartItemsList';
// Hooks
import {
  useAppContext,
  useGetCurrUser,
  useLogout,
  useOnboardingContext,
} from 'hooks';
// Helpers
import { getCorrectCartCount } from 'helpers/cart';
import {
  isUserAthlete,
  isActiveStore,
  isOnboardedStore,
  isUserContentCreator,
  isUserOrganization,
  isUserCustomer,
} from 'helpers/user';
import {
  getMerchShoppingCartFromLocalStorage,
  setFullOnboardingLocalStorageFlag,
} from 'helpers/storage';
import { isActiveRoute } from 'helpers/routes';
import { withCDN } from 'helpers/cdn';
// Constants
import {
  HOME,
  ATHLETES,
  MERCH,
  STREAMS,
  CREATORS,
  PRIVACY_POLICY,
  TERMS_OF_SERVICE,
  DASHBOARD,
  PROFILE,
  MY_SALES,
  MANAGE_MERCH,
  MANAGE_STREAMS,
  PRODUCTS,
  MANAGE_INTERVIEWS,
  EXPERIENCES,
  MANAGE_EXPERIENCES,
  MEDIA_LIBRARY,
  AMAS,
  MANAGE_AMA,
  PURCHASES,
  ACCOUNT_SETTINGS,
  NOTIFICATIONS,
} from 'constants/routes';
import {
  BLOG,
  CAREERS,
  ABOUT_US,
  // ADVERTISE,
  // HOST_A_PPV,
} from 'constants/external-links';
// Ui
import Image from 'ui/Image/Image';
import Collapse from 'ui/Collapse/Collapse';
// Ui2
import Button from 'ui3/Button/Button';
// Components
import UnLoggedUserButtons from 'layouts/Header/UnLoggedUserButtons/UnLoggedUserButtons';
import ShoppingCartButton from 'layouts/Header/ShoppingCartButton/ShoppingCartButton';
import SocialIcons from 'components/common2/SocialIcons/SocialIcons';
import CreatorCard from '../CreatorCard/CreatorCard';
import CreateMenu from 'components/Create/CreateMenu';
import NotificationsButton from '../NotificationsButton/NotificationsButton';
// Styles
import styles from './MobileHeader.module.scss';

export const customerLinks = [
  { label: 'My Profile', href: PROFILE },
  { label: 'Notifications', href: NOTIFICATIONS },
  { label: 'Orders', href: PURCHASES },
  { label: 'Settings', href: ACCOUNT_SETTINGS },
];

type MobileHeaderProps = {
  transparent?: boolean;
  fixed?: boolean;
};

const MobileHeader = ({ transparent, fixed }: MobileHeaderProps) => {
  const { pathname, query } = useRouter();
  const { guestToken } = useAppContext();
  const logout = useLogout();
  const [isMobileMenuOpen, toggleMobileMenu] = useState<boolean>(false);
  const { data } = useGetCurrUser();
  const merchShoppingCart = getMerchShoppingCartFromLocalStorage();
  const { setShowOnboardingModal, setFullOnboarding } = useOnboardingContext();

  const { setSignUpModalVisibility, setSignUpStep } = useAppContext();

  const isLoggedIn = Boolean(data?.me?.id);
  const isAthlete = isUserAthlete(data);

  const isOrganization = isUserOrganization(data);
  const isContentCreator = isUserContentCreator(data);
  const isStore = isAthlete || isOrganization || isContentCreator;
  const isActive = isActiveStore(data);
  const isOnboarded = isOnboardedStore(data);
  const isBasicUser = isUserCustomer(data);
  const canGoLive =
    isActive && (isAthlete || isOrganization || isContentCreator);

  const showStoreLinks = isAthlete || isOrganization || isContentCreator;
  const navRef = useRef<HTMLDivElement | null>(null);

  const onConfirmClick = () => {
    if (isLoggedIn) {
      setShowOnboardingModal(true);
    } else {
      setSignUpStep(0);
      setSignUpModalVisibility(true);
      setFullOnboarding(true);
      // For social sign ups
      setFullOnboardingLocalStorageFlag('true');
    }
  };

  // used to get shopping cart size for guest user
  const { data: merchShoppingCartData } = useQuery<GetMerchCartItemsList>(
    GET_MERCH_CART_ITEM_LIST,
    {
      skip: !guestToken,
    }
  );

  const anonymousCartCount = getCorrectCartCount(merchShoppingCart);
  const guestCartCount = getCorrectCartCount(
    merchShoppingCartData?.getMerchCartItemsList
  );

  const shoppingCartCounter = isLoggedIn
    ? data?.me?.cartSize
    : guestToken
    ? guestCartCount
    : anonymousCartCount;

  const isStoreLinks = isStore
    ? []
    : [
        // Temporarily disable these items
        // { label: 'Advertise with us', href: ADVERTISE, subMenu: null },
        // { label: 'Host a PPV', href: HOST_A_PPV, subMenu: null },
      ];

  const pageLinks = [
    { label: 'Home', href: HOME, subMenu: null },
    { label: 'Athletes', href: ATHLETES, subMenu: null },
    { label: 'Creators', href: CREATORS, subMenu: null },
    {
      label: 'Shop',
      href: null,
      subMenu: [
        {
          label: 'Merch',
          href: {
            pathname: MERCH,
          },
        },
        {
          label: 'Memorabilia',
          href: {
            pathname: PRODUCTS,
          },
        },
        {
          label: 'Experiences',
          href: {
            pathname: EXPERIENCES,
          },
        },
        {
          label: 'Personal Videos',
          href: {
            pathname: AMAS,
          },
        },
      ],
    },
    {
      label: 'Watch',
      href: STREAMS,
      subMenu: null,
    },
    ...isStoreLinks,
    {
      label: 'Company',
      href: null,
      subMenu: [
        {
          label: 'News',
          href: BLOG,
        },
        {
          label: 'About us',
          href: ABOUT_US,
        },
        {
          label: 'Careers',
          href: CAREERS,
        },
      ],
    },
  ];

  const onboardedLinks = isOnboarded
    ? [
        { label: 'My Shop', href: MANAGE_MERCH },
        ...(!isOrganization
          ? [{ label: 'Brand Deals', href: MANAGE_AMA }]
          : []),
        {
          label: 'Interviews',
          href: MANAGE_INTERVIEWS,
        },
      ]
    : [];

  const activeLinks = isActive
    ? [{ label: 'Streams', href: MANAGE_STREAMS }]
    : [];

  const storeLinks = [
    { label: 'Sales', href: MY_SALES },
    ...onboardedLinks,
    ...activeLinks,
    {
      icon: 'user',
      label: 'Experiences',
      href: MANAGE_EXPERIENCES,
    },
    {
      icon: 'upload-file',
      label: 'Media Feed',
      href: MEDIA_LIBRARY,
    },
  ];

  const isActiveProfileMenuLink =
    customerLinks.some((item) => isActiveRoute(pathname, item.href)) ||
    isActiveRoute(pathname, DASHBOARD);

  const handleOnboardingClick = () => {
    setShowOnboardingModal(true);
    toggleMobileMenu(false);
  };

  useEffect(() => {
    const ref = navRef.current;

    if (ref) {
      if (isMobileMenuOpen) {
        disableBodyScroll(ref);
      } else {
        enableBodyScroll(ref);
      }
    }

    return () => {
      if (ref) {
        enableBodyScroll(ref);
      }
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = (): void => {
    toggleMobileMenu((prev) => !prev);
  };

  const handleLogoutButtonClick = () => {
    logout();
  };

  return (
    <div
      className={cn(styles.root, {
        [styles.transparent]: transparent,
        [styles.fixed]: fixed,
        [styles.open]: isMobileMenuOpen,
      })}
    >
      <div className={styles.logoWrapper}>
        <Link href={HOME} passHref prefetch={false}>
          <a>
            <Image
              className={styles.logo}
              src={withCDN('/images/logos/mobile-logo-white.svg')}
              alt="MILLIONS logo"
              title="MILLIONS"
              priority
              unoptimized
            />
          </a>
        </Link>
      </div>
      <div className={styles.menuWrapper}>
        <button
          className={cn(styles.menuToggle, {
            [styles.open]: isMobileMenuOpen,
          })}
          onClick={handleMobileMenuToggle}
          aria-label="toggle-button for mobile menu"
        >
          <span />
          <span />
          <span />
        </button>
        <div className={styles.rightControls}>
          {isLoggedIn ? (
            <>
              {canGoLive ? (
                <CreateMenu />
              ) : (
                isBasicUser && (
                  <Button
                    variant="primary"
                    subVariant="outlined"
                    size="medium"
                    icon="crown"
                    onClick={onConfirmClick}
                  >
                    Go Pro
                  </Button>
                )
              )}
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                size="medium"
                onClick={onConfirmClick}
              >
                Host a Stream
              </Button>
            </>
          )}
          <NotificationsButton />

          <ShoppingCartButton merchCounter={shoppingCartCounter} />
        </div>
      </div>
      <nav
        ref={navRef}
        className={cn(styles.nav, {
          [styles.open]: isMobileMenuOpen,
        })}
      >
        {isLoggedIn && (
          <Collapse
            title={
              <p
                className={cn(styles.parentMenuLink, {
                  [styles.active]: isActiveProfileMenuLink,
                })}
              >
                Profile
              </p>
            }
            className={styles.collapseWrapper}
            bodyClassName={styles.subMenu}
          >
            <ul>
              <li
                key={'dashboard'}
                className={cn(styles.childMenuLink, {
                  [styles.active]: isActiveRoute(pathname, DASHBOARD),
                })}
              >
                <Link href={DASHBOARD} prefetch={false}>
                  <a title="Dashboard">Dashboard</a>
                </Link>
              </li>
              {showStoreLinks &&
                storeLinks.map(({ href, label }) => (
                  <li
                    key={label}
                    className={cn(styles.childMenuLink, {
                      [styles.active]: isActiveRoute(pathname, href),
                    })}
                  >
                    <Link href={href} prefetch={false}>
                      <a>{label}</a>
                    </Link>
                  </li>
                ))}
              <>
                {customerLinks.map(({ href, label }) => (
                  <li
                    key={label}
                    className={cn(styles.childMenuLink, {
                      [styles.active]: isActiveRoute(pathname, href),
                    })}
                  >
                    <Link href={href} prefetch={false}>
                      <a title={label}>{label}</a>
                    </Link>
                  </li>
                ))}
              </>
            </ul>
          </Collapse>
        )}

        {pageLinks.map(({ href, label, subMenu }) => {
          if (subMenu) {
            const isActiveParentMenuLink = subMenu.some((item) =>
              isActiveRoute(
                pathname,
                item.href.pathname,
                item?.href?.query?.productType,
                query?.productType
              )
            );

            return (
              <Collapse
                key={label}
                title={
                  <p
                    className={cn(styles.parentMenuLink, {
                      [styles.active]: isActiveParentMenuLink,
                    })}
                  >
                    {label}
                  </p>
                }
                className={cn(styles.collapseWrapper, {
                  [styles.lastCollapse]: label === 'Company',
                })}
                bodyClassName={styles.subMenu}
              >
                <ul>
                  {subMenu.map((item) => {
                    if (item.href?.pathname?.startsWith('http')) {
                      return (
                        <a
                          title={item.label}
                          key={item.label}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.childMenuLink}
                        >
                          {item.label}
                        </a>
                      );
                    } else {
                      return (
                        <button
                          key={item.label}
                          onClick={handleMobileMenuToggle}
                          className={cn(styles.childMenuLink, {
                            [styles.active]: isActiveRoute(
                              pathname,
                              item.href.pathname,
                              item.href?.query?.productType,
                              query?.productType
                            ),
                          })}
                        >
                          <Link href={item.href} prefetch={false}>
                            <a title={item.label}>{item.label}</a>
                          </Link>
                        </button>
                      );
                    }
                  })}
                </ul>
              </Collapse>
            );
          }

          // Watch button is inverted if user is not a stores(athlete/org/content creator)
          const isInvertedWatchButton = label === 'Watch' && !isStore;

          if (href) {
            return (
              <Link key={label} href={href} prefetch={false}>
                <a
                  target={href.startsWith('http') ? '_blank' : '_self'}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : ''}
                  className={cn(styles.link, {
                    [styles.active]: isActiveRoute(pathname, href),
                    [styles.inverted]: isInvertedWatchButton,
                  })}
                >
                  {label}
                </a>
              </Link>
            );
          }
        })}

        {!isLoggedIn && (
          <UnLoggedUserButtons
            buttonSize="small"
            className={styles.authButtonWrapper}
            buttonClassName={styles.authButton}
            onToggleMobileMenu={toggleMobileMenu}
          />
        )}

        {isLoggedIn && (
          <>
            {isBasicUser && (
              <CreatorCard onConfirmClick={handleOnboardingClick} />
            )}
            <Button
              size="large"
              onClick={handleLogoutButtonClick}
              className={styles.logoutButton}
            >
              Log out
            </Button>
          </>
        )}

        <SocialIcons
          className={styles.socialIconWrapper}
          classNameIcon={styles.socialIcon}
        />

        <div className={styles.externalLinkWrapper}>
          <Link href={PRIVACY_POLICY} prefetch={false}>
            <a className={styles.externalLink}>Privacy policy</a>
          </Link>
          <Link href={TERMS_OF_SERVICE} prefetch={false}>
            <a className={styles.externalLink}>Terms of service</a>
          </Link>
        </div>
      </nav>
    </div>
  );
};

export default MobileHeader;
